import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Building2,
  Users,
  Receipt,
} from 'lucide-react';
import { MarketingDashboard as MarketingDashboardData } from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const formatIDR = (amount: number): string =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(amount);

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Hari ini' },
  { value: 'week', label: 'Minggu ini' },
  { value: 'month', label: 'Bulan ini' },
  { value: 'custom', label: 'Custom' },
];

const MarketingDashboard: React.FC = () => {
  const [data, setData] = useState<MarketingDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<string>('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const params: { period?: string; startDate?: string; endDate?: string } = { period };
      if (period === 'custom' && startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      const res = await api.getMarketingDashboard(params);
      if (reqId === reqIdRef.current) setData(res); // ignore stale responses
    } catch (err: any) {
      if (reqId === reqIdRef.current) {
        console.error('Error loading marketing dashboard:', err);
        setError(err?.response?.data?.message || 'Gagal memuat dashboard.');
      }
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [period, startDate, endDate]);

  useEffect(() => {
    // For custom, only fetch when both dates are set; clear stale data meanwhile.
    if (period === 'custom' && (!startDate || !endDate)) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    load();
  }, [load, period, startDate, endDate]);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marketing Dashboard</h1>
          <p className="mt-2 text-sm text-gray-600">Ringkasan data perusahaan dan langganan.</p>
        </div>
      </div>

      {/* Period Selector */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                period === opt.value
                  ? 'bg-white text-primary-700 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {period === 'custom' && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="py-1.5 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <span className="text-gray-500 text-sm">s/d</span>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="py-1.5 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        )}
      </div>

      {loading && (
        <div className="flex justify-center items-center h-48">
          <LoadingSpinner size="lg" text="Memuat dashboard..." />
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-600">{error}</div>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Period range badge */}
          {data.period && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                {formatDate(data.period.start)} – {formatDate(data.period.end)}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            {/* Current Active Company — live snapshot */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0 rounded-md p-3 bg-blue-500">
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate flex items-center gap-1">
                        Current Active Company
                        <span className="ml-1 inline-block text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded">terkini</span>
                      </dt>
                      <dd className="text-2xl font-semibold text-gray-900">{data.currentActiveCompany}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* Accumulated Subscribed — period-scoped */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0 rounded-md p-3 bg-green-500">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Accumulated Subscribed</dt>
                      <dd className="text-2xl font-semibold text-gray-900">{data.accumulatedSubscribed}</dd>
                    </dl>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <div className="bg-gray-50 rounded p-2 text-center">
                    <p className="text-xs text-gray-500">Launch</p>
                    <p className="text-lg font-bold text-gray-900">{data.subscribedBreakdown.launch}</p>
                  </div>
                  <div className="bg-gray-50 rounded p-2 text-center">
                    <p className="text-xs text-gray-500">Elevate</p>
                    <p className="text-lg font-bold text-gray-900">{data.subscribedBreakdown.elevate}</p>
                  </div>
                  <div className="bg-gray-50 rounded p-2 text-center">
                    <p className="text-xs text-gray-500">Dominate</p>
                    <p className="text-lg font-bold text-gray-900">{data.subscribedBreakdown.dominate}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Paid Subscription Invoices — period-scoped */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0 rounded-md p-3 bg-purple-500">
                    <Receipt className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Paid Subscription Invoices</dt>
                      <dd className="text-2xl font-semibold text-gray-900">{data.paidInvoices.qty}</dd>
                      <dd className="text-sm text-gray-500 mt-1">{formatIDR(data.paidInvoices.amount)}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MarketingDashboard;
